import React from "react";
import {
  TextField,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { uploadToADLS } from "../widgets";
import { renderDocumentFileStatus } from "../formHelpers";
import { BOND_STEP_DOCUMENT_UPLOAD } from "../bondWizardStepPropTypes";
import { CREATION_DOCUMENT_FIELD_NAMES } from "../bondDocumentUploadFieldConfig";
import useBondDocumentUploadState from "../hooks/useBondDocumentUploadState";

const BondDocumentUploadBase = ({
  data,
  updateData,
  sections,
  useFormHelperScroll = false,
  fileNameDisplay = "compact",
}) => {
  const {
    formValues,
    loading,
    handleFileChange,
    handleChange,
    removeConfirmOpen,
    handleRemoveClick,
    handleRemoveConfirm,
    handleRemoveCancel,
  } = useBondDocumentUploadState({
    data,
    updateData,
    fieldNames: CREATION_DOCUMENT_FIELD_NAMES,
    uploadToADLS,
    useFormHelperScroll,
  });

  const renderFileName = (fieldName) => {
    const uploadedFileName = formValues[`${fieldName}FileName`];
    const dataFileName = data[`${fieldName}FileName`];

    if (fileNameDisplay === "legacy") {
      return (
        uploadedFileName ||
        (dataFileName &&
          `File name: ${uploadedFileName || dataFileName}`)
      );
    }

    const fileName = uploadedFileName || dataFileName;
    if (!fileName) return null;
    if (uploadedFileName) return fileName;
    return `File name: ${fileName}`;
  };

  const renderFileSection = ({ fieldName, label }) => (
    <Box>
      <Typography variant="body1" className="mb-2">
        {label}
      </Typography>
      <Button variant="outlined" component="label" fullWidth>
        Browse...{" "}
        <input
          type="file"
          hidden
          name={fieldName}
          accept=".pdf,application/pdf"
          onChange={handleFileChange}
        />
      </Button>
      <Typography variant="body2" color="textSecondary" mt={1}>
        {renderDocumentFileStatus(
          loading[fieldName],
          formValues[fieldName],
          data[`${fieldName}FileName`]
        )}
      </Typography>
      <Typography variant="body2" color="textSecondary" mt={1}>
        {formValues[`${fieldName}Link`] || data[`${fieldName}Link`] ? (
          <a
            href={formValues[`${fieldName}Link`] || data[`${fieldName}Link`]}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Document
          </a>
        ) : (
          "No document link available."
        )}
      </Typography>
      <Typography variant="body2" color="textSecondary" mt={1}>
        {renderFileName(fieldName)}
      </Typography>
      {(formValues[fieldName] ||
        formValues[`${fieldName}Link`] ||
        data[`${fieldName}Link`]) && (
        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={() => handleRemoveClick(fieldName)}
          sx={{ mt: 1 }}
        >
          Remove Attachment
        </Button>
      )}
    </Box>
  );

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Document Upload
      </Typography>

      <Box className="grid grid-cols-1 gap-6">
        {sections.map((section) => (
          <React.Fragment key={section.fieldName}>
            {renderFileSection(section)}
          </React.Fragment>
        ))}

        <TextField
          label="Note"
          name="note"
          value={formValues.note || data.note || ""}
          onChange={handleChange}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          sx={{ mb: 3 }}
        />
      </Box>

      <Dialog open={removeConfirmOpen} onClose={handleRemoveCancel}>
        <DialogTitle>Remove Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this document? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

BondDocumentUploadBase.propTypes = BOND_STEP_DOCUMENT_UPLOAD;

export default BondDocumentUploadBase;
